import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { productsApi } from '../lib/api';

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, categoriesData] = await Promise.all([
          productsApi.getAll(),
          productsApi.getCategories()
        ]);
        setProducts(productsData);
        setCategories(categoriesData.filter(c => c.id !== 'all'));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const newArrivals = products.filter(p => p.is_new).slice(0, 4);
  const saleItems = products.filter(p => p.is_sale).slice(0, 4);

  if (isLoading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-coconut">
        <Loader2 className="h-8 w-8 animate-spin text-accent-ui" />
      </main>
    );
  }

  return (
    <main className="min-h-screen">
      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute w-full h-full object-cover"
          >
            {/* Fashion runway video - served locally */}
            <source 
              src="/fashion-runway.mp4" 
              type="video/mp4" 
            />
          </video>
          {/* Fallback background image if video doesn't load */}
          <img 
            src="https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Fashion background"
            className="absolute inset-0 w-full h-full object-cover -z-10"
          />
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-black/55" />
          {/* Gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        </div>

        {/* Animated light flashes effect */}
        <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
          <div className="flash-effect absolute top-1/4 right-1/4 w-4 h-4 bg-white rounded-full opacity-0" />
          <div className="flash-effect-2 absolute top-1/3 right-1/3 w-3 h-3 bg-white rounded-full opacity-0" />
          <div className="flash-effect-3 absolute top-1/2 right-1/5 w-2 h-2 bg-white rounded-full opacity-0" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
                <Sparkles className="h-4 w-4 text-white" />
                <span className="text-sm text-white/90 font-medium">New Collection 2025</span>
              </div>
              
              <h1 className="font-display text-5xl md:text-7xl font-bold text-white leading-tight drop-shadow-lg">
                Discover
                <br />
                <span className="text-accent-line-solid">Timeless</span>
                <br />
                Elegance
              </h1>
              
              <p className="text-white/80 text-lg max-w-md leading-relaxed">
                Curated fashion pieces that define modern sophistication. Quality craftsmanship meets contemporary design.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/shop">
                  <Button className="bg-white text-text-primary hover:bg-white/90 h-12 px-8 text-base font-semibold shadow-lg">
                    Shop Collection
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button variant="outline" className="h-12 px-8 text-base border-white/50 text-white hover:bg-white/10 backdrop-blur">
                    Our Story
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Price badge floating on right side */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="bg-white/10 backdrop-blur-md p-6 rounded-lg border border-white/20 text-center">
                <p className="text-white/70 text-xs uppercase tracking-wider mb-1">Starting from</p>
                <p className="text-white font-display text-4xl font-bold">$125</p>
                <p className="text-white/60 text-sm mt-2">Premium Collection</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-4">
              Shop by Category
            </h2>
            <p className="text-text-subtle max-w-lg mx-auto">
              Explore our carefully curated collections designed for the modern wardrobe
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/shop?category=${category.id}`}
                className="group relative aspect-square bg-stone-100 rounded-sm overflow-hidden"
              >
                <img
                  src={products.find(p => p.category === category.id)?.image || products[index]?.image}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                  <h3 className="font-display text-xl font-semibold">{category.name}</h3>
                  <p className="text-sm opacity-80">{category.count} items</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      {newArrivals.length > 0 && (
        <section className="py-24 bg-coconut">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-2">
                  New Arrivals
                </h2>
                <p className="text-text-subtle">The latest additions to our collection</p>
              </div>
              <Link to="/shop?filter=new" className="hidden md:flex items-center text-accent-ui hover:underline font-medium">
                View All <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {newArrivals.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
            
            <div className="mt-8 md:hidden text-center">
              <Link to="/shop?filter=new">
                <Button variant="outline" className="border-stone-300">
                  View All New Arrivals
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Featured Banner */}
      <section className="py-24 bg-surface-dark text-text-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="text-accent-line-solid text-sm font-medium uppercase tracking-wider">
                Featured Collection
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
                Autumn / Winter
                <br />
                2025 Collection
              </h2>
              <p className="text-subtle-dark text-lg leading-relaxed max-w-md">
                Embrace the season with our luxurious outerwear and sophisticated layering pieces.
              </p>
              <Link to="/shop">
                <Button className="bg-white text-text-primary hover:bg-stone-100 h-12 px-8 text-base">
                  Explore Collection
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="aspect-[4/3] rounded-sm overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1635205383450-e0fee6fe73c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwbW9kZWx8ZW58MHx8fHwxNzY4MjMzODAzfDA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Winter collection"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sale Section */}
      {saleItems.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-tangerine text-sm font-medium uppercase tracking-wider">
                  Limited Time
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mt-2 mb-2">
                  Sale Items
                </h2>
                <p className="text-text-subtle">Premium pieces at exceptional prices</p>
              </div>
              <Link to="/shop?filter=sale" className="hidden md:flex items-center text-tangerine hover:underline font-medium">
                Shop Sale <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {saleItems.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      <section className="py-24 bg-coconut">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-4">
            Stay in Style
          </h2>
          <p className="text-text-subtle mb-8 max-w-lg mx-auto">
            Subscribe to our newsletter for exclusive access to new arrivals, special offers, and style inspiration.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 h-12 px-4 rounded-sm border border-stone-300 focus:outline-none focus:border-accent-ui bg-white"
            />
            <Button type="submit" className="bg-accent-ui hover:bg-accent-ui/90 text-white h-12 px-8">
              Subscribe
            </Button>
          </form>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
